import { mergeWith } from 'es-toolkit';
import type { ConfigDrivenConfiguration } from './types';

/**
 * Merges multiple campaign configurations together.
 *
 * This function takes multiple campaign configs and merges them, with later configs
 * overriding properties from earlier ones. It handles nested objects by merging
 * them rather than replacing them entirely. Arrays are not merged - they are replaced.
 *
 * @param configs - Array of configurations to merge (spread parameter)
 * @returns A new merged configuration
 * @throws Error if no valid configs are provided
 *
 * @example
 * ```
 * // Merge base config with overrides
 * const finalConfig = mergeConfigs(baseConfig, overrideConfig);
 *
 * // Merge multiple configurations
 * const finalConfig = mergeConfigs(
 *   baseConfig,
 *   globalOverrides,
 *   featureSpecificOverrides
 * );
 * ```
 */
export const mergeConfigs = (
  ...configs: Array<ConfigDrivenConfiguration | Partial<ConfigDrivenConfiguration>>
): ConfigDrivenConfiguration => {
  // Filter out any undefined or null configs
  const validConfigs = configs.filter(Boolean) as Array<ConfigDrivenConfiguration | Partial<ConfigDrivenConfiguration>>;

  if (validConfigs.length === 0) {
    throw new Error('At least one valid config must be provided');
  }

  // Custom merge function that replaces arrays instead of merging them
  const customMerge = (_: unknown, sourceValue: unknown) => {
    // Replace arrays instead of merging them
    if (Array.isArray(sourceValue)) {
      return sourceValue;
    }

    // Return undefined for other types to use the default mergeWith behavior
    return;
  };

  // Apply mergeWith across all configs using reduce
  return validConfigs.slice(1).reduce(
    (result, config) => mergeWith(result, config, customMerge),
    {
      ...validConfigs[0],
    }, // Start with a copy of the first config
  ) as ConfigDrivenConfiguration;
};
